import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  COVERAGE_GAP_NAME_MAX_LENGTH,
  COVERAGE_GAP_NOTE_MAX_LENGTH,
  type CoverageGap,
} from "@shared/types/corpus/api.ts";
import { InlineEditableText } from "../../../../components/primitives/inputs/InlineEditableText";
import { StatisticCard } from "../../../../components/primitives/data-display/StatisticCard";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { formatDate } from "../../../../lib/date";
import { COVERAGE_GAP_REASON_NAMES } from "../../../../lib/coverageGapView";
import { useUrlFilters } from "../../../../lib/url_filters";
import { ROUTES } from "../../../../route_paths";
import { LibraryPage } from "../LibraryPage";
import { CloseCoverageGapDialog } from "./CloseCoverageGapDialog";
import { CoverageGapClaims } from "./CoverageGapClaims";
import { CoverageGapRequesters } from "./CoverageGapRequesters";
import { COVERAGE_GAP_TABS, coverageGapDetailFiltersSchema } from "./coverageGapDetailFilters";
import { useCoverageGap } from "./useCoverageGap";

interface CoverageGapRecheckStatusProps {
  gap: CoverageGap;
}

/** Where the check of a gap closed as filled stands: running, finished or failed. Nothing for a gap
 *  closed for another reason, which is not checked. */
function CoverageGapRecheckStatus({ gap }: CoverageGapRecheckStatusProps) {
  const { closure } = gap;
  if (closure?.reason !== "filled" || closure.recheckJobStatus === null) return null;
  const link = closure.recheckJobId ? (
    <Link
      component={RouterLink}
      to={ROUTES.internal.batchJobs.detail.path({ id: closure.recheckJobId })}
      variant="body2"
    >
      View the check
    </Link>
  ) : null;

  if (isRecheckRunning(gap)) {
    return (
      <Stack direction="row" gap={spacing.snug} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary">
          Checking the claims again…
        </Typography>
        {link}
      </Stack>
    );
  }
  if (closure.recheckJobStatus !== "completed") {
    return <Alert severity="error">The check of the claims did not complete. {link}</Alert>;
  }
  return link;
}

/** The check of a gap closed as filled is queued, running or paused. */
function isRecheckRunning(gap: CoverageGap): boolean {
  const status = gap.closure?.recheckJobStatus;
  return status === "queued" || status === "running" || status === "paused";
}

const RECHECK_POLL_MS = 4000;

interface CoverageGapPageProps {
  gapId: string;
}

function CoverageGapPage({ gapId }: CoverageGapPageProps) {
  const detail = useCoverageGap(gapId);
  const { gap: gapData, updateGap, closeGap, reopenGap, refresh } = detail;
  const gap = gapData.data;
  const [{ tab }, setFilters] = useUrlFilters(coverageGapDetailFiltersSchema);
  const [closing, setClosing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const closed = gap?.closure != null;
  const checking = gap !== null && isRecheckRunning(gap);
  // The check runs in a batch job; the page follows it until it ends.
  useEffect(() => {
    if (!checking) return;
    const timer = setInterval(() => void refresh(), RECHECK_POLL_MS);
    return () => clearInterval(timer);
  }, [checking, refresh]);
  const tabs = COVERAGE_GAP_TABS.filter((entry) => entry.value !== "requesters" || closed);
  const currentTab = tabs.some((entry) => entry.value === tab) ? tab : "claims";

  async function reopen() {
    setActionError(null);
    try {
      await reopenGap();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Something went wrong");
    }
  }

  const cardSpan = { xs: 6, sm: 4, md: "grow" } as const;

  return (
    <LibraryPage
      title={
        gap ? (
          <InlineEditableText
            value={gap.name}
            label="Name"
            required
            maxLength={COVERAGE_GAP_NAME_MAX_LENGTH}
            onSave={(name) => updateGap({ name })}
          />
        ) : (
          "Coverage gap"
        )
      }
      parents={[{ label: "Coverage gaps", to: ROUTES.internal.library.coverage.gaps.path() }]}
      actions={
        gap ? (
          <Stack direction="row" gap={spacing.snug}>
            {gap.closure ? (
              <Button
                variant={gap.demand.claims > 0 ? "contained" : "outlined"}
                onClick={() => void reopen()}
              >
                Reopen gap
              </Button>
            ) : (
              <Button variant="outlined" onClick={() => setClosing(true)}>
                Close gap…
              </Button>
            )}
          </Stack>
        ) : null
      }
    >
      {gapData.loading ? (
        <LoadingState />
      ) : !gap ? (
        <Alert severity="error">{gapData.error ?? "This coverage gap could not be found."}</Alert>
      ) : (
        <Stack gap={spacing.base}>
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
          <Stack direction="row" gap={spacing.snug} alignItems="center">
            <Chip
              label={
                gap.closure ? `Closed · ${COVERAGE_GAP_REASON_NAMES[gap.closure.reason]}` : "Open"
              }
              color={gap.closure ? "default" : "success"}
            />
            <Typography variant="caption" color="text.secondary">
              Created {formatDate(gap.createdAt)}
              {gap.closure ? ` · Closed ${formatDate(gap.closure.closedAt)}` : ""}
            </Typography>
          </Stack>
          <Typography variant="body2" component="div">
            <InlineEditableText
              value={gap.note}
              label="Description"
              multiline
              maxLength={COVERAGE_GAP_NOTE_MAX_LENGTH}
              placeholder="Add a description"
              onSave={(note) => updateGap({ note })}
            />
          </Typography>
          {gap.closure?.note ? (
            <Typography variant="body2" color="text.secondary">
              Closing note: {gap.closure.note}
            </Typography>
          ) : null}
          <CoverageGapRecheckStatus gap={gap} />

          <Grid container spacing={spacing.base}>
            <Grid size={cardSpan}>
              <StatisticCard label="People" mono pad="md">
                {gap.demand.askers.toLocaleString()}
              </StatisticCard>
            </Grid>
            <Grid size={cardSpan}>
              <StatisticCard label="Claims" mono pad="md">
                {gap.demand.claims.toLocaleString()}
              </StatisticCard>
            </Grid>
            <Grid size={cardSpan}>
              <StatisticCard label="Asked" mono pad="md">
                {gap.demand.timesAsked.toLocaleString()}
              </StatisticCard>
            </Grid>
            <Grid size={cardSpan}>
              <StatisticCard label="Last asked" pad="md">
                {formatDate(gap.demand.lastAskedAt)}
              </StatisticCard>
            </Grid>
            {closed ? (
              <Grid size={cardSpan}>
                <StatisticCard label="To tell" mono pad="md">
                  {gap.toTell.toLocaleString()}
                </StatisticCard>
              </Grid>
            ) : null}
          </Grid>

          {tabs.length > 1 ? (
            <Tabs
              value={currentTab}
              onChange={(_event, value: typeof tab) => setFilters({ tab: value })}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {tabs.map(({ value, label }) => (
                <Tab key={value} value={value} label={label} />
              ))}
            </Tabs>
          ) : null}

          {currentTab === "claims" ? <CoverageGapClaims gap={gap} detail={detail} /> : null}
          {currentTab === "requesters" ? (
            <CoverageGapRequesters gapId={gap.id} onChanged={refresh} />
          ) : null}
        </Stack>
      )}

      {gap ? (
        <>
          <CloseCoverageGapDialog
            key={`close-${gap.id}`}
            open={closing}
            onClose={() => setClosing(false)}
            gapName={gap.name}
            onSubmit={closeGap}
          />
        </>
      ) : null}
    </LibraryPage>
  );
}

/** One coverage gap: its figures and, on tabs, its claims and, once it is
 *  closed, the people to tell. */
export default function CoverageGapDetail() {
  const { id } = useParams();
  // Keyed by the id, so moving from one gap to another loads afresh.
  return id ? <CoverageGapPage key={id} gapId={id} /> : null;
}
